import React from 'react';
import immutable from 'immutable';

import Toggle from 'material-ui/Toggle';

import BooleanSearchField from './BooleanSearchField';
import {componentHoc} from 'utils/mixin/componentHoc';
import ConstValues from 'utils/const';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import SearchWidgetDivider from 'components/pure/search/utils/SearchWidgetDivider';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

/**
 * A `BooleanSearchWidgets` renders all the `BooleanSearchField` according to the
 * `field_schema` objects returned in the `fields` field from a `/api/v1/search` endpoint.
 * Notice that all `fields_schema` objects which are boolean search fields are aggregated
 * into a list and rendered together in this widget
 * @deprecated
 */
@componentHoc
class BooleanSearchWidgets extends React.Component {
  /**
   * Get the group id for the widget.
   * @return {string} group_id/search field name when no group
   */
  getWidgetGroupId() {
    const representativeSearchField = this.props.searchFields.first();
    const groupId = representativeSearchField.get('group_id');
    if (groupId === null) {
      return representativeSearchField.name;
    } else {
      return groupId;
    }
  }

  /**
   * Get the group name for the widget.
   * @return {string} group_name/search field name when no group
   */
  getWidgetGroupName() {
    const representativeSearchField = this.props.searchFields.first();
    const groupId = representativeSearchField.get('group_id');
    if (groupId === null) {
      return representativeSearchField.name;
    } else {
      return representativeSearchField.get('group_name');
    }
  }

  /**
   * Return `true` if "boolean group" search widget is toggled; `false` if not.  Groups specified by group_id.
   * @return {boolean} Whether the group of booleans in the search sidebar widget is toggled or not
   */
  booleanGroupIsToggled() {
    if (this.props.toggled) {
      const toggled = this.props.toggled.get(this.getWidgetGroupId());
      return toggled;
    }
    return false;
  }

  onCheckboxChange = (searchField, checked) => {
    SearchFieldsActions.updateSearchFieldsState({
      state: immutable.Map({
        name: searchField.name,
        type: searchField.type,
        checked: checked,
      }),
    });
    SearchFieldsActions.updateBooleanSearchField(searchField, checked);
  };

  /**
   * Callback function for the toggle of the whole boolean search widget
   * @param {object} event An event object
   * @param {boolean} toggled A boolean indicating whether the search widget is toggled or not
   */
  onToggleBooleanSearchWidget = (event, toggled) => {
    SearchFieldsActions.updateSearchWidgetToggleState(ConstValues.booleanWidgetType, toggled, {
      name: this.getWidgetGroupId(),
    });

    setTimeout(() => {
      SearchFieldsActions.updateBooleanSearchExpression(toggled);
    }, ConstValues.delayDurationForSearchWidgetToggles);

    if (toggled) {
      this.props.updateLastToggledOnSearchWidget(this.getWidgetGroupId());
    }
  };

  /**
   * Invoked when the component's DOM representation has been updated.
   * Scrolls toggled on Boolean Search Widgets into view when the lastToggledOnSearchWidget
   * is one of the Boolean Search Widgets.
   */
  componentDidUpdate() {
    if (!this.props.shouldRenderToggled) {
      return;
    }

    if (
      this.booleanGroupIsToggled() &&
      this.getWidgetGroupId() === this.props.lastToggledOnSearchWidget
    ) {
      this.props.scrollWidgetIntoViewIfNecessary();
      // reset the last toggled or it will keep focusing the last component on all future renders.
      this.props.updateLastToggledOnSearchWidget(null);
    }
  }

  /**
   * Return the checked state's boolean value for the search field `searchField`
   * @param  {records.Field} searchField A search field schema
   * @return {bool}    A boolean that specifies whether `searchField` is checked or not
   */
  getBooleanSearchFieldChecked(searchField) {
    const state = this.props.searchFieldsState.get(searchField.name);
    if (state) {
      return state.get('checked');
    }
    return false;
  }

  /**
   * Return a JSX element for a group of boolean search fields
   * @param  {immutable.List} searchFields A list of `SearchField` objects
   * @return {JSX}              A list of annotations
   */
  renderGroupedBooleanSearchFields(searchFields) {
    const classes = cn('search-widget', getSearchFieldClass(this.getWidgetGroupName()));
    return (
      <div className={classes}>
        <Toggle
          ref={this.getWidgetGroupId()}
          label={this.getWidgetGroupName()}
          defaultToggled={false}
          onToggle={this.onToggleBooleanSearchWidget}
          toggled={this.booleanGroupIsToggled()}
        />
        <SearchWidgetDivider />
        {(() => {
          if (this.booleanGroupIsToggled()) {
            return searchFields
              .map((sf, idx) => {
                return (
                  <BooleanSearchField
                    key={sf.name}
                    searchField={sf}
                    checked={this.getBooleanSearchFieldChecked(sf)}
                    onCheckboxChange={this.onCheckboxChange}
                  />
                );
              })
              .toArray();
          } else {
            return null;
          }
        })()}
      </div>
    );
  }

  /**
   * Returns a boolean that indicates whether the Annotations Search Field should not be rendered.
   * @return {boolean} When true indicates that the Annotations Search Field should not be rendered.
   */
  shouldNotRenderGroupedBooleanSearchFields() {
    return !(
      (this.props.shouldRenderToggled && this.booleanGroupIsToggled()) ||
      (this.props.shouldRenderNotToggled && !this.booleanGroupIsToggled())
    );
  }

  render() {
    const searchFields = this.props.searchFields;
    if (searchFields.isEmpty()) return null;

    const annotationsWidget = this.shouldNotRenderGroupedBooleanSearchFields()
      ? null
      : this.renderGroupedBooleanSearchFields(searchFields);

    return (
      <div>
        {/* Render all the rest boolean search widgets */}
        {annotationsWidget}
      </div>
    );
  }
}

export default BooleanSearchWidgets;
